import { zodResolver } from '@hookform/resolvers/zod';
import { OTP_CONFIG } from '@porterin/booking-flow/src/auth/domain/config/otp';
import { authRepo } from '@porterin/booking-flow/src/auth/domain/repo/authRepo';
import {
  otpFormSchema,
  OtpFormValues,
} from '@porterin/booking-flow/src/auth/domain/validations/OtpValidationSchema';
import AuthBanner from '@porterin/booking-flow/src/auth/view/components/common/AuthBanner';
import ChangeMobileSection from '@porterin/booking-flow/src/auth/view/components/otp-modal/ChangeMobileSection';
import ResendOtp from '@porterin/booking-flow/src/auth/view/components/otp-modal/resendOtp';
import { publishAuthCtaTapEvents } from '@porterin/booking-flow/src/auth/view/helpers/AuthAnalyticsHelper';
import {
  GaEvents,
  MoeEvents,
} from '@porterin/booking-flow/src/auth/view/stores/AuthAnalyticsStore';
import {
  AuthAssets,
  AuthTexts,
} from '@porterin/booking-flow/src/auth/view/stores/AuthStore';
import { User } from '@porterin/booking-flow/src/order-review/view/contexts/auth/AuthContext';
import { getTrackingCookieSafe } from '@porterin/booking-flow/src/order-review/view/helpers/CookieHelper';
import {
  makeBkfEventParameterPayload,
  mapPageToCategory,
} from '@porterin/booking-flow/src/utils/BookingFlowAnalyticsHelper';
import { Button, OtpInput } from '@porterin/component-library/src';
import {
  publishEvent,
  publishEventParameter,
} from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import { isDesktop } from '@porterin/lead-flow/src/components/FareEstimateHelper';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ResendOtpOption } from '../../../domain/entities/ResendOtpOption';

import ResendOptionsModal from './ResendOptionsModal';
import { AuthModalConfig } from '../../../domain/types/AuthModalConfig';

export interface OtpModalProps {
  user: User | null;
  onFormSubmit: (otp: string, set_error: (msg: string) => void) => void;
  onChangeMobile: () => void;
  closeModal: () => void;
  config: AuthModalConfig;
}

const OtpModal = ({
  onFormSubmit,
  user,
  onChangeMobile,
  closeModal,
  config,
}: OtpModalProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<OtpFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(otpFormSchema),
    defaultValues: {
      otp: '',
    },
  });
  const [resendOtpTry, setResendOtpTry] = useState(0);
  const [resendOtpTimer, setResendOtpTimer] = useState(OTP_CONFIG.RESEND_TIMER);

  useEffect(() => {
    publishEvent({
      category: mapPageToCategory(window.location.pathname),
      action: GaEvents.pageLand.otp,
    });
    publishEventParameter(
      MoeEvents.pageLand.otp,
      makeBkfEventParameterPayload(),
    );
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setResendOtpTimer(prev => prev - 1);
    }, OTP_CONFIG.TIMER_INTERVAL);

    if (resendOtpTimer === 0) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [resendOtpTimer]);

  const onSubmit = async (data: OtpFormValues) => {
    onFormSubmit(data.otp, (message: string) => setError('root', { message }));
  };

  const onOtpChange = (value: string) => {
    setValue('otp', value, { shouldValidate: true, shouldTouch: true });
  };

  const handleResendClick = async (option: ResendOtpOption) => {
    publishEvent({
      category: mapPageToCategory(window.location.pathname),
      action: `${GaEvents.resendSubmit} - ${resendOtpTry + 1}`,
    });

    try {
      await authRepo.resendOtp(
        getTrackingCookieSafe(),
        user?.mobile || '',
        option,
      );
    } catch (error) {
      setError('root', {
        message: typeof error === 'string' ? error : 'Something went wrong',
      });
    }
    setResendOtpTry(prev => prev + 1);
    setResendOtpTimer(OTP_CONFIG.RESEND_TIMER);
  };

  const onResendOtp = async (option: ResendOtpOption = 'SMS') => {
    if (!isDesktop()) {
      setIsModalOpen(true);
    } else {
      handleResendClick(option);
    }
  };
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div
      data-testid="otp-modal"
      id="otp-modal"
      className={`w-screen h-screen flex flex-col
				space-y-10 items-center bg-white p-6 sm:p-0
				text-typography-primary
				sm:w-[800px] sm:h-[610px] sm:rounded-[20px]
				sm:flex-row sm:space-y-0 sm:items-start
			`}
    >
      <AuthBanner show_mobile_banner={false} auth_state="otp" />
      <Image
        src={AuthAssets.crossBlackIcon.src}
        className="absolute top-8 right-8 cursor-pointer max-lg:hidden"
        width={24}
        height={24}
        alt={AuthAssets.crossBlackIcon.alt}
        onClick={closeModal}
      />
      <div
        className={`flex flex-col space-y-6
										sm:space-y-12 w-full sm:w-7/12
										sm:px-14 sm:py-16
										items-start justify-start h-full
								`}
      >
        <div className="flex flex-col space-y-6 items-start w-full">
          <div className="text-typography-primary text-title-base font-semibold sm:text-display-2xl">
            {AuthTexts.otpModal.title}
          </div>
          <ChangeMobileSection
            mobile={user?.mobile}
            on_change={onChangeMobile}
            mobile_code={config.mobileCode}
          />
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col space-y-6 w-full lg:justify-between gap-[2rem] h-full"
        >
          <div className="flex w-full flex-col sm:justify-between">
            <OtpInput
              identifiers={{ id: 'otp' }}
              inputOptions={{ error: errors?.otp?.message }}
              interactions={{ onChange: onOtpChange }}
              {...register('otp')}
            />
            <div className="mt-10 hidden sm:block">
              <ResendOtp
                resendOtpTimer={resendOtpTimer}
                resendOtpTry={resendOtpTry}
                onResendOtp={onResendOtp}
              />
            </div>
          </div>

          <div className="flex flex-col space-y-3 sm:flex-col-reverse sm:align-end">
            {errors.root?.message && (
              <div className="text-[#eb5757] text-lg font-semibold whitespace-nowrap">
                {errors.root?.message}
              </div>
            )}
            <Button
              type="submit"
              className="bg-[#0A57FF] text-typography-primary-inverse font-copy-lg font-semibold sm:mt-3"
              interactions={{
                onClick: () => {
                  publishAuthCtaTapEvents(
                    mapPageToCategory(window.location.pathname),
                    GaEvents.formSubmit.otp,
                    'otp',
                  );
                },
              }}
              identifiers={{ id: 'otp-submit-btn' }}
            >
              Verify my account
            </Button>

            <div className="sm:hidden">
              <ResendOtp
                resendOtpTimer={resendOtpTimer}
                resendOtpTry={resendOtpTry}
                onResendOtp={onResendOtp}
              />
            </div>
          </div>
        </form>
      </div>
      {isModalOpen ? (
        <ResendOptionsModal
          is_open={isModalOpen}
          on_close={() => setIsModalOpen(false)}
          on_resend={(option: ResendOtpOption) => {
            handleResendClick(option);
            setIsModalOpen(false);
          }}
        />
      ) : null}
    </div>
  );
};

export default OtpModal;
